import { lazy, ComponentType, LazyExoticComponent, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";

// Eagerly loaded (small, always needed)
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import NotFound from "./pages/NotFound";

/**
 * lazy() wrapper that survives a Cloudflare Pages deploy. When a new
 * deploy lands while a tab is open, the cached HTML can reference JS
 * chunk hashes that no longer exist on the CDN — dynamic imports then
 * 404 forever. We catch that specific failure and force one full
 * window.location.reload() so the browser fetches the fresh HTML and
 * the new chunk hashes that go with it.
 *
 * Throttled to one reload per minute via sessionStorage so we never
 * loop if the actual issue is unrelated to chunk versioning.
 */
const RELOAD_THROTTLE_MS = 60 * 1000;
const RELOAD_KEY = 'app:lastChunkReload';

function lazyWithChunkReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message ?? '');
      const looksLikeChunkError =
        msg.includes('Failed to fetch dynamically imported module') ||
        msg.includes('Importing a module script failed') ||
        msg.includes('error loading dynamically imported module');

      if (looksLikeChunkError) {
        const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
        if (Date.now() - last > RELOAD_THROTTLE_MS) {
          window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
          window.location.reload();
          // Return a never-resolving promise so Suspense keeps showing the
          // loader instead of letting React see the rejected import.
          return new Promise<{ default: T }>(() => {});
        }
      }
      throw err;
    }
  });
}

// Lazy-loaded: Auth pages
const ForgotPassword = lazyWithChunkReload(() => import("./pages/ForgotPassword"));
const ResetPassword = lazyWithChunkReload(() => import("./pages/ResetPassword"));

// Lazy-loaded: Client Portal
const ClientPortal = lazyWithChunkReload(() => import("./pages/ClientPortal"));
const ClientChildren = lazyWithChunkReload(() => import("./pages/ClientChildren"));
const ClientSubscription = lazyWithChunkReload(() => import("./pages/ClientSubscription"));
const ClientHistory = lazyWithChunkReload(() => import("./pages/ClientHistory"));
const ClientProfile = lazyWithChunkReload(() => import("./pages/ClientProfile"));

// Lazy-loaded: Staff Portal
const StaffDashboard = lazyWithChunkReload(() => import("./pages/StaffDashboard"));
const StaffInventory = lazyWithChunkReload(() => import("./pages/StaffInventory"));
const StaffClients = lazyWithChunkReload(() => import("./pages/StaffClients"));
const StaffRenewals = lazyWithChunkReload(() => import("./pages/StaffRenewals"));
const StaffBundler = lazyWithChunkReload(() => import("./pages/StaffBundler"));
const StaffShipments = lazyWithChunkReload(() => import("./pages/StaffShipments"));
const StaffReturns = lazyWithChunkReload(() => import("./pages/StaffReturns"));
const StaffPayments = lazyWithChunkReload(() => import("./pages/StaffPayments"));
const StaffPaymentReports = lazyWithChunkReload(() => import("./pages/StaffPaymentReports"));
const StaffFullCopyList = lazyWithChunkReload(() => import("./pages/StaffFullCopyList"));
const StaffLentOutBooks = lazyWithChunkReload(() => import("./pages/StaffLentOutBooks"));
const AdminPanel = lazyWithChunkReload(() => import("./pages/AdminPanel"));
const StaffPricing = lazyWithChunkReload(() => import("./pages/StaffPricing"));
const StaffCatalogs = lazyWithChunkReload(() => import("./pages/StaffCatalogs"));
const StaffLeads = lazyWithChunkReload(() => import("./pages/StaffLeads"));
const StaffRecyclingBin = lazyWithChunkReload(() => import("./pages/StaffRecyclingBin"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refire every query when the user alt-tabs back to the app.
      // Reduces churn (and removed a class of crashes) since this isn't a
      // realtime dashboard; staff explicitly refresh when they need new data.
      refetchOnWindowFocus: false,
      // Give cached data a 30s grace period before being considered stale,
      // so quick in-app navigation doesn't trigger immediate refetches.
      staleTime: 30 * 1000,
    },
  },
});

function SuspenseFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<SuspenseFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* Client Portal Routes */}
                <Route path="/portal" element={<ClientPortal />} />
                <Route path="/portal/children" element={<ClientChildren />} />
                <Route path="/portal/subscription" element={<ClientSubscription />} />
                <Route path="/portal/history" element={<ClientHistory />} />
                <Route path="/portal/profile" element={<ClientProfile />} />
                {/* Staff Portal Routes */}
                <Route path="/staff" element={<StaffDashboard />} />
                <Route path="/staff/inventory" element={<StaffInventory />} />
                <Route path="/staff/clients" element={<StaffClients />} />
                <Route path="/staff/renewals" element={<StaffRenewals />} />
                <Route path="/staff/bundler" element={<StaffBundler />} />
                <Route path="/staff/shipments" element={<StaffShipments />} />
                <Route path="/staff/returns" element={<StaffReturns />} />
                <Route path="/staff/payments" element={<StaffPayments />} />
                <Route path="/staff/reports" element={<StaffPaymentReports />} />
                <Route path="/staff/reports/copies" element={<StaffFullCopyList />} />
                <Route path="/staff/reports/lent-out" element={<StaffLentOutBooks />} />
                <Route path="/staff/users" element={<AdminPanel />} />
                <Route path="/staff/admin" element={<AdminPanel />} />
                <Route path="/staff/pricing" element={<StaffPricing />} />
                <Route path="/staff/catalogs" element={<StaffCatalogs />} />
                <Route path="/staff/leads" element={<StaffLeads />} />
                <Route path="/staff/trash" element={<StaffRecyclingBin />} />
                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
